/**
 * crm-redaction-policy.ts
 *
 * Role-Aware Field-Level Redaction & Multi-Tenant Authorization Guard (Sprint CRM-01):
 * - Enforces hard multi-tenant separation.
 * - Masks sensitive financial fields (taxpayer TIN, credit risk tiers) for standard clerk/support roles.
 * - Redacts confidential notes and restricted timeline items unless explicitly authorized.
 */

import type {
  CrmPerson,
  CrmOrganization,
  CrmPersonOnePagerView,
  CrmTimelineItem,
} from '../crm-contracts.js';

export interface CrmUserRoleContext {
  readonly principalId: string;
  readonly tenantId: string;
  readonly roles: readonly ('admin' | 'finance' | 'sales_rep' | 'clerk' | 'support')[];
  readonly capabilities: readonly string[];
}

export class CrmRedactionPolicy {
  public static canViewFinancials(ctx: CrmUserRoleContext): boolean {
    return ctx.roles.includes('admin') || ctx.roles.includes('finance') || ctx.capabilities.includes('crm.financial.read');
  }

  public static canManageConsent(ctx: CrmUserRoleContext): boolean {
    return ctx.roles.includes('admin') || ctx.roles.includes('sales_rep') || ctx.capabilities.includes('crm.consent.manage');
  }

  public static canReviewAiSuggestions(ctx: CrmUserRoleContext): boolean {
    return ctx.roles.includes('admin') || ctx.roles.includes('sales_rep') || ctx.capabilities.includes('crm.suggestion.review');
  }

  /**
   * Applies role-aware field masking to an organization record
   */
  public static redactOrganization(
    org: CrmOrganization,
    ctx: CrmUserRoleContext
  ): CrmOrganization {
    if (org.tenantId !== ctx.tenantId) {
      throw new Error(`AUTHORIZATION_ERROR: Cross-tenant access violation for tenant '${org.tenantId}'`);
    }

    if (this.canViewFinancials(ctx)) {
      return org;
    }

    // Redact restricted financial fields
    return {
      ...org,
      taxIdentifier: org.taxIdentifier ? '***-RESTRICTED-***' : undefined,
      creditRiskTier: undefined,
    };
  }

  /**
   * Redacts restricted timeline items from standard sales/clerk feeds
   */
  public static redactTimeline(
    timeline: readonly CrmTimelineItem[],
    ctx: CrmUserRoleContext
  ): readonly CrmTimelineItem[] {
    const hasFinancialAccess = this.canViewFinancials(ctx);

    return timeline.map((item) => {
      if (item.isRestricted && !hasFinancialAccess) {
        return {
          ...item,
          title: '🔒 Restricted Financial / Risk Update',
          description: '[Redacted under field-level role policy]',
        };
      }
      return item;
    });
  }

  /**
   * Projects a role-redacted single-response person one-pager view
   */
  public static projectOnePagerView(
    view: CrmPersonOnePagerView,
    ctx: CrmUserRoleContext
  ): CrmPersonOnePagerView {
    if (view.person.tenantId !== ctx.tenantId) {
      throw new Error(`AUTHORIZATION_ERROR: Cross-tenant access violation for tenant '${view.person.tenantId}'`);
    }

    const canFin = this.canViewFinancials(ctx);
    const canConsent = this.canManageConsent(ctx);
    const canAi = this.canReviewAiSuggestions(ctx);

    const redactedOrg = view.organization ? this.redactOrganization(view.organization, ctx) : undefined;
    const redactedTimeline = this.redactTimeline(view.timeline, ctx);

    return {
      ...view,
      organization: redactedOrg,
      timeline: redactedTimeline,
      viewPermissions: {
        canEdit: ctx.roles.includes('admin') || ctx.roles.includes('sales_rep'),
        canViewFinancials: canFin,
        canManageConsent: canConsent,
        canReviewAiSuggestions: canAi,
      },
    };
  }
}
